import {setIsPlaying, setLoadingSequence, setIsPlayingMix} from 'actions/index.js';
import ChannelStripEffects from './channel/ChannelStripEffects.js';
import MultiMix from './mix/MultiMix.js';
import Tone from 'tone';
import sampleRateChooser from './SampleRateChooser.js';
import ScriptableSequencerEngine from 'engine/ScriptableSequencer.js';
import Scripting from 'scripting/Scripting.js';

const MAX_TRACKS = 8;

class MultiSequencerEngine {
    constructor(store) {
        this.store = store;

        // map of sequenceId => sequencer engines synced together
        this.idToSequencerEngine = {};

        // the sequencer engine that triggers other sequencer
        // engines to play on step 0.
        this.headSequencerEngine = undefined;

        this.sequencerEnginesToPlay = [];

        this.sequenceIdQueue = [];

        window.sequencerEngine = this;

        this.initAubio();
    }

    getHead() {
        return this.idToSequencerEngine[Object.keys(
            this.idToSequencerEngine)[0]];
    }

    get state() {
        return this.store.getState();
    }

    get trackStep() {
        let trackStep = 0;
        for (let id in this.idToSequencerEngine) {
            let _trackStep = this.idToSequencerEngine[id].trackStepReal;
            if (_trackStep > trackStep) {
                return _trackStep;
            }
        }
        return trackStep;
    }

    initAubio() {
        window.Aubio().then((aubio) => {
            this.aubio = aubio;
            this.initSamplerTrack();
        });
    }
 
    set sampleRate(s) {
        sampleRateChooser.setSampleRate(s);
    }

    stop() {
        for (let id in this.idToSequencerEngine) {
            this.idToSequencerEngine[id].stop();
        }
        Tone.Transport.stop();
    }

    updateBpm(bpm) {
        for (let id in this.idToSequencerEngine) {
            this.idToSequencerEngine[id].updateBpm(bpm);
        }
    }

    getSequencerEngine(id) {
        if (id === undefined) {
            return this.idToSequencerEngine[Object.keys(this.idToSequencerEngine)[0]];
        }
        if (id === -1 || id === '-1') {
            return this.samplerTrack;
        }
        return this.idToSequencerEngine[id];
    }

    // in order to schedule multipl sequencer engines, synced together
    // we have the first sequencer engine (called the headSequencerEngine),
    // call this function at step 0/step 15. We then schedule a play
    // for any sequener engines that are set to play, using the time
    // passed to this function (i.e. when that step 0/15 will be)
    stepOneCallback(time) {
        this.sequencerEnginesToPlay.forEach(s => {
            let ticks = Tone.Transport.getTicksAtTime(time);
            let transportTime = new Tone.TransportTime(ticks, 'i');
            s.play(transportTime);
        });
        this.sequencerEnginesToPlay = [];
    }

    getSamplerPlaySequence() {
        this.samplerTrack.trackStep = 0;
        this.samplerTrack.playing = true;
        return this.samplerTrack.playSequence;
    }

    isNothingPlaying() {
        return this.getSequencesPlaying().length === 0;
    }

    getSequencesPlaying(playing=true) {
        let isPlaying = this.store.getState().sequencer.isPlaying;
        return Object.keys(this.idToSequencerEngine).filter(
            id => {
                if (playing === false && isPlaying[id] === undefined) {
                    return true;
                } else {
                    return isPlaying[id] === playing;
                }
            });
    }

    unloadTrack(sequenceId, dispose=true) {
        let sequencerEngine = this.idToSequencerEngine[sequenceId];
        if (sequencerEngine.playing) {
            // i.e. stop the playing sequence
            sequencerEngine.stop();
        }
        delete this.idToSequencerEngine[sequenceId];

        let i = this.sequenceIdQueue.indexOf(sequenceId);
        if (i > -1) {
            this.sequenceIdQueue.splice(i, 1);
        }
        this.store.dispatch(setIsPlaying(sequenceId, undefined));
        if (dispose) {
            setTimeout(() => {
                sequencerEngine.dispose();
            }, 500);
        }
    }

    async loadTrack(id) {
        let sequencesNotPlaying = this.getSequencesPlaying(false);
        if (this.sequenceIdQueue.length > (MAX_TRACKS - 1)) {
            if (sequencesNotPlaying.length > 0) {
                this.unloadTrack(sequencesNotPlaying[0]);
            } else {
                let sequenceId = this.sequenceIdQueue.shift();
                this.unloadTrack(sequenceId);
            }
        }

        this.store.dispatch(setLoadingSequence(id, true));
        let sequencerEngine = new ScriptableSequencerEngine(
            this.store, true, undefined, this.aubio);

        sequencerEngine.channelMixer.connectMixEffects();
        let scripting = new Scripting(sequencerEngine, true);
        sequencerEngine.setScripting(scripting);
        sequencerEngine.multiSequencerEngine = this;
        sequencerEngine.loadedSequenceId = id;
        sequencerEngine.setMasterVolume(1);
        this.sequenceIdQueue.push(id);
        this.idToSequencerEngine[id] = sequencerEngine;
        this.store.dispatch(setIsPlaying(id, false));
        await sequencerEngine.loadSequence(id);
        /*
        sequencerEngine.channelMixer.mixEffects.auxSend.connect(
            this.auxEffects.input);
            */
    }

    initSamplerTrack() {
        if (!this.auxEffects) {
            this.auxEffects = new ChannelStripEffects(
                Tone.Master, this);
        }
        let sequencerEngine = new ScriptableSequencerEngine(
            this.store, true, undefined, this.aubio);
        sequencerEngine.channelMixer.connectMixEffects();
        let scripting = new Scripting(sequencerEngine, true);
        sequencerEngine.setScripting(scripting);
        sequencerEngine.channelMixer.mixEffects.auxSend.connect(
            this.auxEffects.input);
        sequencerEngine.setMasterVolume(0);
        sequencerEngine.loadNewSequence();

        this.samplerTrack = sequencerEngine;
        this.samplerTrack.isSampler = true;
        this.samplerTrack.loadedSequenceId = -1;
        this.changeVolume(this.samplerTrack, 0);
    }

    sequenceRolling() {
        for (let id in this.idToSequencerEngine) {
            let e = this.idToSequencerEngine[id];
            if (e.isSequenceRolling) {
                return true;
            }
        }
        return false;
    }

    // plays a sequence (if none are playing), if one is already playing
    // then it queues it up to be played on the next bar.
    play(sequence) {
        let sequencerEngine = this.getSequencerEngine(sequence && sequence.id);
        if (sequencerEngine !== undefined) {
           if (sequencerEngine.playing && this.getSequencesPlaying(true).length === 1) {
               this.samplerTrack.playing = false;
               this.samplerTrack.playSequence.stop();
               this.samplerTrack.playSequence.cancel();
               this.samplerTrack.loadNewSequence();
           }
            if (sequence && sequence.playing) {
                this.setHeadSequencerEngine(undefined);
            } 
            let sequencesPlaying = this.getSequencesPlaying(true);
            if (this.getSequencesPlaying(true).length == 0 || sequencerEngine.playing) {
                this.store.dispatch(setIsPlayingMix(true));
                if (!sequencerEngine.playing && sequencesPlaying.length == 0) {
                    this.setHeadSequencerEngine(sequencerEngine);
                }
                sequencerEngine.play();
            } else {
                this.sequencerEnginesToPlay.push(sequencerEngine);
            }

            sequencesPlaying = sequencesPlaying.map(x => this.idToSequencerEngine[x])
                .filter(s => s !== sequencerEngine);
            if (sequencesPlaying.length > 0 &&
                sequencesPlaying.every(s => s.stepOneCallback === undefined)) {
                this.setHeadSequencerEngine(sequencesPlaying[0]);
            }
        }
   }

    setHeadSequencerEngine(engine) {
        if (engine === undefined && this.headSequencerEngine !== undefined) {
            this.headSequencerEngine.stepOneCallback = undefined;
            this.headSequencerEngine.getSamplerPlaySequence = undefined;
            return;
        }
        this.headSequencerEngine = engine;
        this.headSequencerEngine.stepOneCallback = (t) => this.stepOneCallback(t);
        this.headSequencerEngine.getSamplerPlaySequence = (t) => this.getSamplerPlaySequence(t);
    }
}

export default MultiSequencerEngine;
